import type { Moment } from 'moment'
import { z } from 'zod'
import type { CountryNameType } from '../../../../src/types/common'
import type { Timestamp } from '../../../../src/types/firebase'
import { JobPositionNames } from '../../../../src/types/jobTypes'
import type { ShiftTypeBasic } from '../../../../src/types/jobs'
import { MIN_WAGES } from '../../shared-config'
import moment from '../../util/moment'

export const POLICY_START_DATE = '2019-09-15'
export const useNewPricing = (date: Date | Timestamp) => moment(POLICY_START_DATE).isBefore(moment(date))

export type ChargesOfCountry = {
  normal: number
  holiday: number
}

export const JobCostConstants = {
  MIN_SALARY_HOURLY: 195,
  MIN_POOL_SALARY_HOURLY: 185,
  MIN_HOLIDAY_SALARY_HOURLY: 250,
  MIN_FREELANCE_SALARY_HOURLY: 240,

  MANYMORE_CHARGE_RATE: 0.049, // per total hours
  INSURANCE_FEE: 1.6, // per hour, subtracted from staffer wage - (not actually being used)

  SOCIAL_RATE_NOR: 0.141, // norway 14.1% - added to business cost (without manymore fee)
  SOCIAL_RATE_SWE: 0.3142, // sweden 31.42% - added to business cost (without manymore fee)

  SERVICE_CHARGE: {
    // keep the keys in order starting from most recent on top
    '2024-06-02': {
      Norway: {
        normal: 84,
        holiday: 98,
      },
      Sweden: {
        normal: 69,
        holiday: 49,
      },
    } as Record<CountryNameType, ChargesOfCountry>,
    [POLICY_START_DATE]: {
      Norway: {
        normal: 69,
        holiday: 98,
      },
      Sweden: {
        normal: 69,
        holiday: 49,
      },
    } as Record<CountryNameType, ChargesOfCountry>,
  },
  // Service charge based on total wage (1.0 = 100%)
  SERVICE_CHARGE_RATE: 0.18, // no longer used
}

const getSocialCostRate = (countrySocialCost: number) =>
  Math.ceil(
    100 * // round up to 2 decimal places
      (1 + JobCostConstants.MANYMORE_CHARGE_RATE) *
      (1 + countrySocialCost)
  ) /
    100 -
  1
// this is what we present as socialCost for business
// but it actually also includes the Payout Partner fee
export const socialCostRateByCountry: Record<CountryNameType, number> = {
  Norway: getSocialCostRate(JobCostConstants.SOCIAL_RATE_NOR),
  Sweden: getSocialCostRate(JobCostConstants.SOCIAL_RATE_SWE),
}

type SinceType = keyof typeof JobCostConstants.SERVICE_CHARGE

export const getHourlyServiceChargeByCountry = (
  country = 'Norway',
  isHoliday = false,
  startDate: Date | Moment = new Date()
): number => {
  const since: SinceType =
    (Object.keys(JobCostConstants.SERVICE_CHARGE) as Array<SinceType>).find((key) =>
      moment(startDate).isAfter(moment(key))
    ) ?? (Object.keys(JobCostConstants.SERVICE_CHARGE)[0] as SinceType)
  const chargesOfCountry =
    JobCostConstants.SERVICE_CHARGE[since][country as CountryNameType] ?? JobCostConstants.SERVICE_CHARGE[since].Norway
  if (isHoliday) {
    return chargesOfCountry.holiday
  }
  return chargesOfCountry.normal
}

export const getDefaultServiceCharge = ({
  country = 'Norway',
  isHoliday = false,
  startDate = new Date(),
}: {
  country?: string
  isHoliday?: boolean
  startDate?: Date | Moment
}) => {
  const since: SinceType =
    (Object.keys(JobCostConstants.SERVICE_CHARGE) as Array<SinceType>).find((key) =>
      moment(startDate).isAfter(moment(key))
    ) ?? (Object.keys(JobCostConstants.SERVICE_CHARGE)[0] as SinceType)
  const chargesOfCountry =
    JobCostConstants.SERVICE_CHARGE[since][country as CountryNameType] ?? JobCostConstants.SERVICE_CHARGE[since].Norway
  return isHoliday ? chargesOfCountry.holiday : chargesOfCountry.normal
}

export const getHourlyServiceChargeInternalTemp = ({
  groupServiceCharge,
  businessServiceCharge,
  isInternalTemp = false,
  country = 'Norway',
  isHoliday = false,
  startDate = new Date(),
}: {
  groupServiceCharge?: number | null
  businessServiceCharge?: number | null
  isInternalTemp?: boolean
  country?: string
  isHoliday?: boolean
  startDate?: Date | Moment
}) => {
  const newShiftsDate = moment('2024-12-10')
  const hasGroupCharge = isInternalTemp && groupServiceCharge !== null && groupServiceCharge !== undefined
  const hasBusinessCharge = businessServiceCharge !== null && businessServiceCharge !== undefined
  const defaultServiceCharge = getDefaultServiceCharge({ country, isHoliday, startDate })
  if (moment(startDate).isSameOrAfter(newShiftsDate)) {
    if (hasGroupCharge) {
      return groupServiceCharge
    }
    if (hasBusinessCharge) {
      return businessServiceCharge
    }
    return defaultServiceCharge
  }
  return defaultServiceCharge
}

// This was previously under SOCIAL_RATE / SOCIAL_RATE_SWE
// should no longer be used
export const legacySocialCostRateByCountry: Record<CountryNameType, number> = {
  Norway: 0.2023, // 1.20481927711 on some places
  Sweden: 0.375,
}

export const jobSkillSchema = z.object({
  wageMin: z.number(),
  wageMax: z.number(),
  skill: z.enum(['low', 'medium', 'high']),
})

export type JobTypeSkill = z.infer<typeof jobSkillSchema>

export const jobTypeSkillWagesMap = z.record(z.array(jobSkillSchema))

export type JobTypesSkills = z.infer<typeof jobTypeSkillWagesMap>

type PartialJobType = {
  createdAt: Timestamp
  salaryCurrency: string
  salaryHourly: number
  isHolidayJob: boolean
}
export type JobDataType = PartialJobType & {
  shifts: Array<ShiftTypeBasic>
}

export type JobCostInfoType = {
  isFree?: boolean
  salaryCurrency: string
  salaryHourly: number
  serviceCharge: number
  serviceChargeRate: number // per hour
  serviceChargeTitle: string
  totalCostWithCharge: number
  totalSalary: number
  totalWorkingHours: number
}

// Create a new type that includes 'unskilled'
export type WageButtonPositions = JobPositionNames | 'unskilled'

// Update the type annotation for WAGE_BUTTONS
export const WAGE_BUTTONS: Record<WageButtonPositions, { low: number; standard: number; high: number }> = {
  unskilled: {
    low: MIN_WAGES.no.minimumWage,
    standard: MIN_WAGES.no.minimumWage + 10,
    high: MIN_WAGES.no.minimumWage + 40,
  },
  Cashier: {
    low: MIN_WAGES.no.minimumWage,
    standard: MIN_WAGES.no.minimumWage + 10,
    high: MIN_WAGES.no.minimumWage + 40,
  },
  Waiter: {
    low: MIN_WAGES.no.Waiter.defaultWage,
    standard: MIN_WAGES.no.Waiter.defaultWage + 20,
    high: MIN_WAGES.no.Waiter.defaultWage + 40,
  },
  'Host/Hostess': {
    low: MIN_WAGES.no['Host/Hostess'].defaultWage,
    standard: MIN_WAGES.no['Host/Hostess'].defaultWage + 20,
    high: MIN_WAGES.no['Host/Hostess'].defaultWage + 40,
  },
  'Kitchen Assistant': {
    low: MIN_WAGES.no['Kitchen Assistant'].defaultWage,
    standard: MIN_WAGES.no['Kitchen Assistant'].defaultWage + 10,
    high: MIN_WAGES.no['Kitchen Assistant'].defaultWage + 30,
  },
  Housekeeper: {
    low: MIN_WAGES.no['Housekeeper'].defaultWage,
    standard: MIN_WAGES.no['Housekeeper'].defaultWage + 15,
    high: MIN_WAGES.no['Housekeeper'].defaultWage + 35,
  },
  Barista: {
    low: MIN_WAGES.no['Barista'].defaultWage,
    standard: MIN_WAGES.no['Barista'].defaultWage + 10,
    high: MIN_WAGES.no['Barista'].defaultWage + 30,
  },
  Chef: {
    low: MIN_WAGES.no['Chef'].defaultWage,
    standard: MIN_WAGES.no['Chef'].defaultWage + 20,
    high: MIN_WAGES.no['Chef'].defaultWage + 50,
  },
  'Event Bartender': {
    low: MIN_WAGES.no['Event Bartender'].defaultWage,
    standard: MIN_WAGES.no['Event Bartender'].defaultWage + 10,
    high: MIN_WAGES.no['Event Bartender'].defaultWage + 30,
  },
  Pizzaiolo: {
    low: MIN_WAGES.no['Pizzaiolo'].defaultWage,
    standard: MIN_WAGES.no['Pizzaiolo'].defaultWage + 20,
    high: MIN_WAGES.no['Pizzaiolo'].defaultWage + 50,
  },
  Cleaner: {
    low: MIN_WAGES.no['Cleaner'].defaultWage,
    standard: MIN_WAGES.no['Cleaner'].defaultWage + 15,
    high: MIN_WAGES.no['Cleaner'].defaultWage + 25,
  },
  Bartender: {
    low: MIN_WAGES.no.Bartender.defaultWage,
    standard: MIN_WAGES.no.Bartender.defaultWage + 20,
    high: MIN_WAGES.no.Bartender.defaultWage + 40,
  },
  Barback: {
    low: MIN_WAGES.no.Barback.defaultWage,
    standard: MIN_WAGES.no.Barback.defaultWage + 5,
    high: MIN_WAGES.no.Barback.defaultWage + 20,
  },
  Runner: {
    low: MIN_WAGES.no.Runner.defaultWage,
    standard: MIN_WAGES.no.Runner.defaultWage + 5,
    high: MIN_WAGES.no.Runner.defaultWage + 20,
  },
  Dishwasher: {
    low: MIN_WAGES.no.Dishwasher.defaultWage,
    standard: MIN_WAGES.no.Dishwasher.defaultWage + 5,
    high: MIN_WAGES.no.Dishwasher.defaultWage + 20,
  },
  Wardrobe: {
    low: MIN_WAGES.no.Wardrobe.defaultWage,
    standard: MIN_WAGES.no.Wardrobe.defaultWage + 10,
    high: MIN_WAGES.no.Wardrobe.defaultWage + 20,
  },
  Cover: {
    low: MIN_WAGES.no.Cover.defaultWage,
    standard: MIN_WAGES.no.Cover.defaultWage + 10,
    high: MIN_WAGES.no.Cover.defaultWage + 20,
  },
  'Event Crew': {
    low: MIN_WAGES.no['Event Crew'].defaultWage,
    standard: MIN_WAGES.no['Event Crew'].defaultWage + 10,
    high: MIN_WAGES.no['Event Crew'].defaultWage + 20,
  },
}
